/**
 * Cmarkdownquadrant Component
 * Renders a mermaid `quadrantChart` as an SVG scatter in a four-quadrant box
 * inside the shared Cmarkdownvisual chrome: parse → pure layout → SVG body,
 * matching the sibling mermaid renderers (sequence/timeline).
 */

'use client';

import type { CSSProperties, ReactElement } from 'react';
import { useMemo } from 'react';
import {
  Cmarkdownvisual,
  CmarkdownvisualError,
  CmarkdownvisualErrorReason,
} from '../Cmarkdownvisual';
import { parseMermaidQuadrant } from './parse-mermaid-quadrant';
import { buildQuadrantLayout } from './quadrant-layout';
import { QuadrantCanvas } from './QuadrantCanvas';
import { QuadrantCompactPreview } from './QuadrantCompactPreview';
import { QUADRANT_METRIC_UNIT, QUADRANT_VISUAL_TYPE } from './quadrant-constants';

const CONTENT_MIN_HEIGHT = 380;

export interface CmarkdownquadrantProps {
  content: string;
  className?: string;
  style?: CSSProperties;
}

export function Cmarkdownquadrant({
  content,
  className,
  style,
}: CmarkdownquadrantProps): ReactElement {
  const parsed = useMemo(() => parseMermaidQuadrant(content), [content]);
  const layout = useMemo(
    () => (parsed ? buildQuadrantLayout(parsed) : null),
    [parsed],
  );

  if (!parsed || !layout) {
    return (
      <CmarkdownvisualError
        type={QUADRANT_VISUAL_TYPE}
        reason={CmarkdownvisualErrorReason.Unparseable}
      />
    );
  }

  if (parsed.points.length === 0) {
    return (
      <CmarkdownvisualError
        type={QUADRANT_VISUAL_TYPE}
        reason={CmarkdownvisualErrorReason.Empty}
      />
    );
  }

  return (
    <Cmarkdownvisual
      type={QUADRANT_VISUAL_TYPE}
      title={parsed.title}
      contentStyle={{ minHeight: CONTENT_MIN_HEIGHT, flex: 1 }}
      className={className}
      style={style}
      compactPreview={<QuadrantCompactPreview points={parsed.points} />}
      metric={{ value: parsed.points.length, unit: QUADRANT_METRIC_UNIT }}
    >
      <QuadrantCanvas layout={layout} title={parsed.title} />
    </Cmarkdownvisual>
  );
}
